/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { useCallback, useState } from 'react';

import { jsx } from '@compiled/react';
import { defineMessages, useIntl } from 'react-intl-next';

import { cssMap } from '@atlaskit/css';
import ShowMoreHorizontalIcon from '@atlaskit/icon/core/show-more-horizontal';
import { ButtonMenuItem } from '@atlaskit/navigation-system';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import ItemWrapper from '../MenuItem/item-wrapper';

const styles = cssMap({
	textStyle: {
		fontWeight: token('font.weight.medium'),
		fontFamily: token('font.family.body'),
		color: token('color.text.subtle'),
	},
});

export const showMoreMessages = defineMessages({
	showMore: {
		id: 'platform.navigation-experiences.universal-create.show-more',
		defaultMessage: 'More',
	},
});

export interface ShowMoreButtonProps {
	heading?: string;
	onClick: () => void;
	showAll: boolean;
}

export default function ShowMoreButton({ onClick, heading, showAll }: ShowMoreButtonProps) {
	const { formatMessage } = useIntl();

	const [expanded, setExpanded] = useState(false);

	const handleClick = useCallback(() => {
		setExpanded(true);
		onClick();
	}, [onClick]);

	// Hide the button if showAll is true or if it has been clicked (expanded)
	if (showAll || expanded) {
		return null;
	}

	return (
		<ItemWrapper>
			<ButtonMenuItem
				elemBefore={<ShowMoreHorizontalIcon label="" color={token('color.text.subtle')} />}
				onClick={handleClick}
				testId="show-more-button"
				aria-label={`Show more${heading ? ` ${heading}` : ''} options`}
			>
				<Box as="span" xcss={styles.textStyle}>
					{formatMessage(showMoreMessages.showMore)}
				</Box>
			</ButtonMenuItem>
		</ItemWrapper>
	);
}
